import { useCallback, useEffect, useMemo } from 'react';

import useGiftWithPurchaseItemsQuery from 'api/queries/useGiftWithPurchaseItemsQuery';
import ProductCardV2 from 'features/shop/ProductCardV2';
import Modal from 'honeydew/Modal';
import { trackEvent } from 'utils/analyticsUtils';

import { getSortedInStockGiftWithPurchaseItems } from './giftWithPurchaseUtils';

const GIFT_WITH_PURCHASE_MODAL_ID = 'gift-with-purchase-modal';
const GIFT_WITH_PURCHASE_MODAL_NAME = "Don't forget your free gift";
const GIFT_WITH_PURCHASE_MODAL_TYPE = 'GWP_MODAL';

/**
 * @param {number} index - Zero-based position in the sorted GWP list
 * @returns {Object} ProductCardV2 metadata for the GWP picker modal
 */
function getGiftWithPurchaseModalProductCardMeta(index) {
    return {
        carouselID: GIFT_WITH_PURCHASE_MODAL_ID,
        carouselName: GIFT_WITH_PURCHASE_MODAL_NAME,
        carouselPosition: index,
        carouselType: GIFT_WITH_PURCHASE_MODAL_TYPE,
        isCarouselModalCard: true,
    };
}

/**
 * GiftWithPurchaseModal — full-screen style modal for choosing a free GWP item.
 * Uses the same catalog and ordering as GiftWithPurchaseCarousel.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 */
function GiftWithPurchaseModal({ onClose, open }) {
    const { data: giftItems = [], isError, isLoading } = useGiftWithPurchaseItemsQuery();

    const sortedItems = useMemo(() => getSortedInStockGiftWithPurchaseItems(giftItems), [giftItems]);

    const canShowModal = !isLoading && !isError && sortedItems.length > 0;

    useEffect(() => {
        if (open && canShowModal) {
            trackEvent('GWP v2 modal viewed');
        }
    }, [open, canShowModal]);

    const handleClose = useCallback(() => {
        trackEvent('GWP v2 modal dismissed');
        onClose();
    }, [onClose]);

    if (isLoading || isError || sortedItems.length === 0) {
        return null;
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            bgColor="bg-lime if:bg-strawberry"
            headline={
                <h2 className="text-heading-xs mb-15 flex min-h-30 items-center pr-40 md:mb-20 md:pr-45">
                    Don&apos;t forget to choose your free gift!
                </h2>
            }
            styles={{
                contentRoot: 'm-[10px] rounded-md outline-none pt-15 md:m-0 md:pt-15',
            }}
        >
            <div className="text-black">
                <div className="grid grid-cols-2 gap-10 md:grid-cols-4 md:gap-15">
                    {sortedItems.map((item, index) => (
                        <ProductCardV2
                            key={item.id}
                            data={item}
                            layout="vertical"
                            variant="GIFTWITHPURCHASE"
                            metaData={getGiftWithPurchaseModalProductCardMeta(index)}
                        />
                    ))}
                </div>
            </div>
        </Modal>
    );
}

export default GiftWithPurchaseModal;
